import React from 'react';
import { X, Info, Check, CheckCircle2, AlertCircle } from 'lucide-react';

interface SizeGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SizeGuideModal: React.FC<SizeGuideModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col relative animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-black italic text-[#061970] uppercase tracking-tighter">Guía de Tallas</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-8 pb-4">
            
            {/* Step 1: Mide Tu Cabeza */}
            <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">1</div>
                <div className="space-y-3 pt-1 flex-1">
                    <div>
                        <h3 className="text-sm font-black text-[#061970] uppercase">MIDE TU CABEZA</h3>
                        <p className="text-xs text-gray-600 leading-relaxed mt-1">
                            Mide la circunferencia de tu cabeza. Pasa la cinta métrica por encima de las cejas y alrededor de la parte más ancha de tu cabeza.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                        <div className="bg-gray-50 rounded-lg p-3 flex gap-3 items-start border border-gray-100 flex-1">
                            <Info className="w-4 h-4 text-[#459EDF] mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">CONSEJO PRO</p>
                                <p className="text-[11px] text-gray-600 leading-tight">
                                    Si no tienes cinta métrica, usa un cordón y luego mídelo con una regla.
                                </p>
                            </div>
                        </div>
                        <div className="w-[128px] h-[159px] bg-gray-200 rounded-lg flex-shrink-0 mx-auto md:mx-0">
                            <img src='/guia_de_tallas/Cabeza_medidas.png'></img>
                        </div>
                    </div>
                </div>
            </div>

            {/* Step 2: Forma de la Cabeza */}
            <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">2</div>
                <div className="space-y-4 pt-1 flex-1 w-full">
                    <div>
                        <h3 className="text-sm font-black text-[#061970] uppercase">FORMA DE LA CABEZA</h3>
                        <p className="text-xs text-gray-600 leading-relaxed mt-1">
                            Identifica la forma de tu cabeza (vista superior). Nuestros cascos están diseñados principalmente para formas <span className="font-bold text-gray-800">Intermedias</span>.
                        </p>
                    </div>
                    <div className="flex justify-between items-end px-2 sm:px-8">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-gray-300"></div>
                            <span className="text-[10px] text-gray-500 font-bold uppercase">REDONDA</span>
                        </div>
                        <div className="flex flex-col items-center gap-2 relative -top-1">
                            <div className="absolute -top-6 text-[#FF7F32]">
                                <div className="w-4 h-4 rounded-full border border-[#FF7F32] flex items-center justify-center">
                                    <Check className="w-3 h-3" />
                                </div>
                            </div>
                            <div className="w-10 h-14 sm:w-12 sm:h-16 rounded-[24px] border-2 border-[#FF7F32] bg-[#FF7F32]/5"></div>
                            <span className="text-[10px] text-[#FF7F32] font-bold uppercase">INTERMEDIA</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-14 h-10 sm:w-16 sm:h-12 rounded-[24px] border border-gray-300"></div>
                            <span className="text-[10px] text-gray-500 font-bold uppercase">LARGA</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Step 3: Tabla de Medidas */}
            <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">3</div>
                <div className="space-y-3 pt-1 flex-1 w-full overflow-hidden">
                    <h3 className="text-sm font-black text-[#061970] uppercase">TABLA DE MEDIDAS</h3>
                    <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
                        <table className="w-full text-xs text-left">
                            <thead className="bg-gray-50 uppercase text-gray-500 font-bold border-b border-gray-100 text-[10px]">
                                <tr>
                                    <th className="px-3 py-2 w-1/3">TALLA</th>
                                    <th className="px-3 py-2 w-1/3">PULGADAS</th>
                                    <th className="px-3 py-2 w-1/3">CENTÍMETROS</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {[
                                    { size: 'CHICO (S)', in: '21 1/2" - 22"', cm: '55 - 56 CM' },
                                    { size: 'MEDIANO (M)', in: '22 1/4" - 22 3/4"', cm: '57 - 58 CM' },
                                    { size: 'GRANDE (L)', in: '23" - 23 1/2"', cm: '59 - 60 CM' },
                                    { size: 'X-G (XL)', in: '23 3/4" - 24 1/2"', cm: '61 - 62 CM' },
                                    { size: 'XX-G (XXL)', in: '24 3/4" - 25 3/16"', cm: '63 - 64 CM' }
                                ].map((row) => (
                                    <tr key={row.size} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-3 py-2.5 font-bold text-[#061970] text-[11px]">{row.size}</td>
                                        <td className="px-3 py-2.5 text-gray-600 font-medium text-[11px]">{row.in}</td>
                                        <td className="px-3 py-2.5 font-bold text-[#FF7F32] text-[11px]">{row.cm}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Step 4: Comprueba el Ajuste */}
            <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">4</div>
                <div className="space-y-3 pt-1 flex-1">
                    <div>
                        <h3 className="text-sm font-black text-[#061970] uppercase">COMPRUEBA EL AJUSTE</h3>
                        <p className="text-xs text-gray-600 leading-relaxed mt-1">
                            El casco no debe moverse al girar la cabeza bruscamente.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                        <div className="bg-[#459EDF]/5 rounded-lg p-3 flex gap-3 items-start border border-[#459EDF]/20 flex-1">
                            <Info className="w-4 h-4 text-[#459EDF] mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-[10px] font-bold text-[#061970] uppercase mb-0.5">Prueba de "Mejillas de Ardilla"</p>
                                <p className="text-[11px] text-gray-600 leading-tight mb-2">
                                    Debes sentir las almohadillas presionando ligeramente tus mejillas hacia arriba.
                                </p>
                                <div className="flex items-center gap-1.5 text-[9px] font-black text-[#459EDF] uppercase tracking-wide">
                                    <CheckCircle2 className="w-3 h-3" />
                                    <span>LAS ALMOHADILLAS CEDEN TRAS 15-20 HORAS</span>
                                </div>
                            </div>
                        </div>
                        <div className="w-[224px] h-[211px] bg-gray-200 rounded-lg flex-shrink-0 mx-auto md:mx-0">
                             <img src='/guia_de_tallas/Casco_medidas.png'></img>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Alert */}
            <div className="bg-[#FF7F32] rounded-lg p-3 flex items-center justify-center gap-2 text-white shadow-md mt-4">
                <AlertCircle className="w-4 h-4" />
                <span className="text-xs font-black uppercase tracking-wide">¡MIDE BIEN TU CABEZA ANTES DE ORDENAR!</span>
            </div>

        </div>
      </div>
    </div>
  );
};
